import nookies, { setCookie as setNookie, parseCookies } from 'nookies';
import isUndefined from 'lodash/isUndefined';
import env from './env';
import { NO_PUPIL_DOOR } from './pupilDoor';
import { SCHOOL_SSO_LOGGED_OUT_COOKIE_NAME } from './schoolSso';
import {
  GDPR_STATISTICS_ENABLED,
  GDPR_FUNCTIONAL_ENABLED,
  GDPR_CONSENT_GIVEN,
  PREFERRED_LOGIN_VARIANT,
} from './constants';

const { GDPR_VERSION } = env;

export const getCookieDomain = (ctx?: any): string => {
  let host: string;

  if (typeof window !== 'undefined') {
    host = window.location.hostname; // Use hostname to exclude port
  } else if (ctx && ctx.req) {
    host = (ctx.req.headers.host || '').split(':')[0]; // Remove port
  } else {
    return '';
  }

  if (!host || host === 'localhost') {
    return 'localhost';
  }

  // IP-literal hosts (e.g. the Android emulator's 10.0.2.2 alias for the dev
  // machine): a cookie can't be scoped to an IP via a Domain attribute, and the
  // dot-splitting below would emit a garbage domain like ".0.2.2" that the
  // browser silently rejects — so the cookie never persists and cookie-gated
  // redirects (e.g. /country) loop forever. Return '' for a host-only cookie,
  // which works for any IP. Local-dev only; real domains keep the behaviour below.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return '';
  }

  const parts = host.split('.');

  // For multi-part domains like toosa.dev.somistech.fi, return .dev.somistech.fi
  if (parts.length > 2) {
    return `.${parts.slice(1).join('.')}`;
  }

  // For two-part domains like somistech.fi, return .somistech.fi
  return `.${host}`;
};

// Callers that pass no expiry (gdprVersion, onboarding, previouslyLoggedIn,
// preferLoginVariant) used to get the max 32-bit date — Jan 2038, i.e.
// permanent. The published cookie policy caps persistent cookies, so default to
// one year, the same lifetime as the cookie-consent cookies.
export const DEFAULT_EXPIRE_DAYS = 365;

export const getExpireTime = (days?) => {
  const date = new Date();
  const expireDays = isUndefined(days) ? DEFAULT_EXPIRE_DAYS : days;
  date.setTime(date.getTime() + expireDays * 24 * 60 * 60 * 1000);
  return date;
};

const setCookie = async (name, value, expireTimeInDays?): Promise<object> =>
  await setNookie(null, name, value, {
    expires: getExpireTime(expireTimeInDays),
    domain: getCookieDomain(),
    path: '/',
  });

// toosa's `returnPath`, written from peikko so a Skolon deep link can name
// the section to open. Deliberately NOT setServerSideCookie: that defaults to
// DEFAULT_EXPIRE_DAYS (365), and a year-long redirect cookie on the shared
// registrable domain would send every later login on this browser — any user,
// any method — to a stale path. Lifetime only has to cover one SSO round
// trip. toosa's own middleware writes the same cookie with maxAge 60.
export const RETURN_PATH_COOKIE = 'returnPath';
export const RETURN_PATH_MAX_AGE_SECONDS = 600;

export const setReturnPathCookie = (value: string): void => {
  nookies.set(null, RETURN_PATH_COOKIE, value, {
    maxAge: RETURN_PATH_MAX_AGE_SECONDS,
    domain: getCookieDomain(),
    path: '/',
    sameSite: 'lax',
    secure:
      typeof window !== 'undefined' && window.location.protocol === 'https:',
  });
};

// Abandoned or rejected sign-ins must not leave a redirect behind for the
// next person on a shared device.
export const clearReturnPathCookie = (): void => {
  nookies.destroy(null, RETURN_PATH_COOKIE, {
    domain: getCookieDomain(),
    path: '/',
  });
};

// FALLBACK store for the from=app hand-off budget (see /login-skolon, which
// counts in sessionStorage first). A cookie is browser-wide, so it cannot
// tell one tab from another and is the wrong primary: it would charge a
// second tab for the first one's hand-off. But it cannot be UNAVAILABLE
// where this is read, which sessionStorage can be in a private window or a
// partitioned context — reaching that code required a token cookie that was
// written and read back. So it bounds the loop where the precise store is
// refused, and nowhere else.
export const SKOLON_HANDOFF_COOKIE = 'skolonHandoff';
// Bounds the budget in time, so someone who legitimately comes back an hour
// later is not still carrying a spent one. A redirect loop turns over in
// milliseconds, far inside this.
export const SKOLON_HANDOFF_MAX_AGE_SECONDS = 300;

export const getSkolonHandoffCount = (): number => {
  try {
    const count = Number(parseCookies()[SKOLON_HANDOFF_COOKIE]);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch (e) {
    return 0;
  }
};

export const setSkolonHandoffCount = (count: number): void => {
  nookies.set(null, SKOLON_HANDOFF_COOKIE, String(count), {
    maxAge: SKOLON_HANDOFF_MAX_AGE_SECONDS,
    domain: getCookieDomain(),
    path: '/',
    sameSite: 'lax',
    secure:
      typeof window !== 'undefined' && window.location.protocol === 'https:',
  });
};

// DEV-794 — /login-school's loop budgets fall back to a cookie where
// sessionStorage is refused, exactly as the Skolon hand-off budget above
// does and for the same reasons. One cookie per budget, named by the caller.
// Short-lived: a redirect loop turns over in seconds, and a pupil who comes
// back later must not be carrying a spent budget.
export const SCHOOL_SSO_BUDGET_MAX_AGE_SECONDS = 300;

export const getSchoolSsoBudgetCount = (name: string): number => {
  try {
    const count = Number(parseCookies()[name]);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch (e) {
    return 0;
  }
};

export const setSchoolSsoBudgetCount = (name: string, count: number): void => {
  nookies.set(null, name, String(count), {
    maxAge: SCHOOL_SSO_BUDGET_MAX_AGE_SECONDS,
    domain: getCookieDomain(),
    path: '/',
    sameSite: 'lax',
    secure:
      typeof window !== 'undefined' && window.location.protocol === 'https:',
  });
};

export const setServerSideCookie = async (
  ctx,
  name,
  value,
  expireTimeInDays?,
): Promise<object> =>
  await nookies.set(ctx, name, value, {
    expires: getExpireTime(expireTimeInDays),
    domain: getCookieDomain(ctx),
    path: '/',
  });

export const setToken = async (
  value: string,
  expireTimeInDays = 1,
): Promise<void> => {
  await setCookie('token', value, expireTimeInDays);
};

// UK student auth — remember the school the user selected so they
// skip the selector on subsequent visits. Spec §1.4: 1-year expiry,
// scoped to .somebuddy.com (same Domain logic as every other peikko
// cookie via getCookieDomain).
export const SCHOOL_COOKIE_NAME = 'somebuddy_school';
export const setSchool = async (
  slug: string,
  expireTimeInDays = 365,
): Promise<void> => {
  await setCookie(SCHOOL_COOKIE_NAME, slug, expireTimeInDays);
};

// Paired cookie that caches the school's auth_model so _app.tsx can
// route UK users to the right login page without an identify lookup
// on every page render. Set alongside SCHOOL_COOKIE_NAME at selector
// time.
//
// Stale-data note: if a school changes auth_model (rare ops event),
// this cookie goes stale for already-visited users until either the
// cookie expires (1y) or they re-visit /school. Acceptable for the
// pilot; document a force-clear in the school-auth-change runbook
// if/when that ever happens.
export const SCHOOL_AUTH_MODEL_COOKIE_NAME = 'somebuddy_school_auth_model';
export const setSchoolAuthModel = async (
  authModel: string,
  expireTimeInDays = 365,
): Promise<void> => {
  await setCookie(SCHOOL_AUTH_MODEL_COOKIE_NAME, authModel, expireTimeInDays);
};

// DEV-735 — the pupil door configured for this school, cached at selector
// time exactly like the auth_model cookie above and with the same staleness
// caveat.
//
// Separate from auth_model on purpose: that field says how a pupil comes to
// hold a voucher, this one says how they prove who they are. See identify's
// docs/adr/0001. Values come from identify's school lookup
// (roster_phrase | passwordless | microsoft).
//
// Absent means no pupil door is configured for this school, and nothing may
// substitute one. That substitution is the bug being removed here: a
// voucher_self school had no branch anywhere and its pupils quietly landed
// on the Finnish phone-number form.
export const SCHOOL_PUPIL_LOGIN_COOKIE_NAME = 'somebuddy_school_pupil_login';
export const setSchoolPupilLogin = async (
  method: string,
  expireTimeInDays = 365,
): Promise<void> => {
  await setCookie(SCHOOL_PUPIL_LOGIN_COOKIE_NAME, method, expireTimeInDays);
};

// Must also be called when switching schools: leaving the previous value
// behind would pair the new school's slug with the old school's door.
export const clearSchoolPupilLogin = async (
  expireTimeInDays = 365,
): Promise<void> => {
  await setCookie(
    SCHOOL_PUPIL_LOGIN_COOKIE_NAME,
    NO_PUPIL_DOOR,
    expireTimeInDays,
  );
};


export const setUser = (value, expireTimeInDays = 14) => {
  setCookie('user', JSON.stringify(value), expireTimeInDays);
};

export const setGdpr = () => {
  setCookie('gdprVersion', GDPR_VERSION);
};

export const setCookiesConsentGiven = (): void => {
  setCookie(GDPR_CONSENT_GIVEN, GDPR_VERSION, 365);
};

export const resetCookiesConsent = (): void => {
  setCookie(GDPR_CONSENT_GIVEN, '', 0);
};

export const enableStatistics = (): void => {
  setCookie(GDPR_STATISTICS_ENABLED, true, 365);
};

export const enableFunctional = async (): Promise<void> => {
  await setCookie(GDPR_FUNCTIONAL_ENABLED, true, 365);
};

export const disableStatistics = (): void => {
  setCookie(GDPR_STATISTICS_ENABLED, false, 365);
};

export const disableFunctional = (): void => {
  setCookie(GDPR_FUNCTIONAL_ENABLED, false, 365);
};

export const setLocale = (locale) => {
  setCookie('locale', locale, 28);
};

export const setCountry = (country) => {
  setCookie('country', country, 28);
};

export const setOnboarding = (status) => {
  setCookie('onboarding', status);
};

// DEV-794 — the client-side writer for the school-SSO cookie lists in
// utils/schoolSso: the locale /school pre-sets for a single-Google-door
// school, and the terms/consent/onboarding cookies /login-school writes
// once a pupil is activated (⚠️ needs legal/DPA sign-off before prod; see
// getSchoolSsoConsentCookies).
export const presetSchoolSsoCookies = async (
  cookies: { name: string; value: string; days: number }[],
): Promise<void> => {
  await Promise.all(
    cookies.map(({ name, value, days }) => setCookie(name, value, days)),
  );
};

// DEV-794 — cleared by /login-school on a successful activation, so the
// next `/` auto-starts SSO again. Written server-side by /logout; see
// SCHOOL_SSO_LOGGED_OUT_COOKIE_NAME in utils/schoolSso.
export const clearSchoolSsoLoggedOut = async (): Promise<void> => {
  await setCookie(SCHOOL_SSO_LOGGED_OUT_COOKIE_NAME, '', 0);
};

export const setPreferLoginVariantCookie = (variant: string): void => {
  setServerSideCookie(null, PREFERRED_LOGIN_VARIANT, variant);
};

export const setPreviouslyLoggedIn = (state: boolean): void => {
  setCookie('previouslyLoggedIn', state);
};

const getCookiesAsObject = (): any =>
  `; ${document.cookie}`.split('; ').reduce((acc, value) => {
    const parts = value.split('=');
    return parts.length < 2 ? acc : { ...acc, [parts[0].trim()]: parts[1] };
  }, {});

export const getUserCookie = () => {
  try {
    return JSON.parse(getCookiesAsObject().user);
  } catch (e) {
    return null;
  }
};

export const getCountryCookie = () => {
  const innerDoc = document || null;
  if (innerDoc) {
    return getCookiesAsObject().country;
  }
  return null;
};

export const getFunctionalCookie = (): boolean | null => {
  try {
    return getCookiesAsObject()[GDPR_FUNCTIONAL_ENABLED] === 'true';
  } catch (e) {
    return null;
  }
};

// getFunctionalCookie compares against 'true', so it collapses two different
// states into false: never asked, and explicitly declined. The Help entry has
// to tell them apart, or it re-prompts a user who already said no on every
// click. 'false' is not a new value here — disableFunctional already writes it
// from the cookie settings page.
export const getFunctionalCookieDecision = (): 'true' | 'false' | null => {
  try {
    const value = getCookiesAsObject()[GDPR_FUNCTIONAL_ENABLED];
    return value === 'true' || value === 'false' ? value : null;
  } catch (e) {
    return null;
  }
};

export const getStatisticsCookie = (): boolean | null => {
  try {
    return getCookiesAsObject()[GDPR_STATISTICS_ENABLED] === 'true';
  } catch (e) {
    return null;
  }
};

export const getCookieConsent = (): string | null => {
  try {
    return getCookiesAsObject()[GDPR_CONSENT_GIVEN];
  } catch (e) {
    return null;
  }
};

export const getToken = (): string => {
  try {
    const { token } = parseCookies();
    return token;
  } catch (e) {
    return null;
  }
};

export const checkTermsAccepted = (termsCookieValue: string): boolean =>
  termsCookieValue === GDPR_VERSION;

export const isCookiesConsentGiven = (cookieValue: string): boolean =>
  cookieValue === GDPR_VERSION;
